import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react";
import { FaPlus, FaTrash } from "react-icons/fa";
import { crm, Pill } from "../../../../theme";
import { NveAttribute, ProductNveEntry } from "../../../../models/Nve";
import { getNveAttributes, getProductNve, saveProductNve } from "../../../../services/nveService";
import { formatNcm } from "../../../process/components/tableStyles";

type Pick = { spec: string; description: string };
type Manual = { attr: string; spec: string; description: string };

const labelProps = { fontSize: "12px", fontWeight: "600", color: crm.textMuted, mb: "4px" };
const inputProps = { h: "42px", borderRadius: "10px", focusBorderColor: crm.brand };

/**
 * Seletor de NVE por produto (NCM + descrição). Atributos e especificações vêm da tabela NVE do NCM;
 * sem tabela para o NCM, permite digitar atributo (2 letras) e especificação (4 dígitos) à mão.
 * Salva direto no catálogo (vale para todos os processos com o mesmo NCM + produto).
 */
const NveModal = ({
  isOpen,
  onClose,
  ncm,
  product,
}: {
  isOpen: boolean;
  onClose: () => void;
  ncm: string;
  product: string;
}) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attributes, setAttributes] = useState<NveAttribute[]>([]);
  const [picks, setPicks] = useState<Record<string, Pick>>({});
  const [manual, setManual] = useState<Manual[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([getNveAttributes(ncm), getProductNve(ncm, product)])
      .then(([attrs, saved]) => {
        if (cancelled) return;
        const known = new Set(attrs.map((a) => a.code));
        const initial: Record<string, Pick> = {};
        const extra: Manual[] = [];
        saved.attributes.forEach((s) => {
          if (known.has(s.attributeCode))
            initial[s.attributeCode] = { spec: s.specificationCode, description: s.description ?? "" };
          else extra.push({ attr: s.attributeCode, spec: s.specificationCode, description: s.description ?? "" });
        });
        setAttributes(attrs);
        setPicks(initial);
        setManual(extra);
      })
      .catch(() => toast({ title: "Não foi possível carregar a NVE", status: "error" }))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [isOpen, ncm, product]);

  const updateManual = (index: number, patch: Partial<Manual>) =>
    setManual((rows) => rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const save = async () => {
    const entries: ProductNveEntry[] = [
      ...Object.entries(picks)
        .filter(([, p]) => p.spec)
        .map(([attributeCode, p]) => ({
          attributeCode,
          specificationCode: p.spec,
          description: p.description,
        })),
      ...manual
        .filter((m) => m.attr.trim() || m.spec.trim())
        .map((m) => ({
          attributeCode: m.attr.trim().toUpperCase(),
          specificationCode: m.spec.trim(),
          description: m.description,
        })),
    ];
    setSaving(true);
    try {
      await saveProductNve(ncm, product, entries);
      toast({ title: "NVE salva", status: "success" });
      onClose();
    } catch (error: any) {
      const data = error?.response?.data;
      const message = data?.notifications?.[0] ?? (typeof data === "string" ? data : undefined);
      toast({ title: "Não foi possível salvar a NVE", description: message, status: "error" });
    } finally {
      setSaving(false);
    }
  };

  const hasReference = attributes.length > 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(2px)" />
      <ModalContent borderRadius="16px" boxShadow={crm.shadowLg}>
        <ModalHeader borderBottom={`1px solid ${crm.border}`}>
          <Text fontSize="18px" fontWeight="700" color={crm.text}>
            NVE do produto
          </Text>
          <Text fontSize="13px" fontWeight="400" color={crm.textMuted}>
            {formatNcm(ncm.replace(/\D/g, ""))} · {product || "sem descrição"}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody py="20px">
          {loading ? (
            <Flex justify="center" py="40px">
              <Spinner color={crm.brand} />
            </Flex>
          ) : (
            <>
              <Box border={`1px solid ${crm.border}`} borderRadius={crm.radius} p="16px" mb="16px">
                <Text fontSize="14px" fontWeight="700" color={crm.text} mb="12px">
                  Atributos do NCM
                </Text>
                {!hasReference && (
                  <Text fontSize="13px" color={crm.textFaint}>
                    A tabela NVE não tem este NCM. Cadastre a tabela em Configurações › NVE ou informe os códigos
                    manualmente abaixo.
                  </Text>
                )}
                {attributes.map((attr) => {
                  const pick = picks[attr.code];
                  return (
                    <Flex key={attr.code} gap="12px" mb="12px" direction={{ base: "column", md: "row" }}>
                      <Box flex="1">
                        <Text {...labelProps}>
                          <Pill>{attr.code}</Pill> {attr.name ?? "Atributo"}
                        </Text>
                        <Select
                          {...inputProps}
                          placeholder="Não informar"
                          value={pick?.spec ?? ""}
                          onChange={(e) => {
                            const spec = e.target.value;
                            setPicks((p) => {
                              const next = { ...p };
                              if (!spec) delete next[attr.code];
                              else next[attr.code] = { spec, description: "" };
                              return next;
                            });
                          }}
                        >
                          {attr.specifications.map((s) => (
                            <option key={s.code} value={s.code}>
                              {s.code} · {s.name ?? ""}
                            </option>
                          ))}
                        </Select>
                      </Box>
                      {pick && (
                        <Box flex="1">
                          <Text {...labelProps}>Descrição (opcional)</Text>
                          <Input
                            {...inputProps}
                            value={pick.description}
                            placeholder={attr.specifications.find((s) => s.code === pick.spec)?.name ?? ""}
                            maxLength={500}
                            onChange={(e) =>
                              setPicks((p) => ({ ...p, [attr.code]: { ...pick, description: e.target.value } }))
                            }
                          />
                        </Box>
                      )}
                    </Flex>
                  );
                })}
              </Box>

              <Box border={`1px solid ${crm.border}`} borderRadius={crm.radius} p="16px">
                <Flex justify="space-between" align="center" mb="12px">
                  <Text fontSize="14px" fontWeight="700" color={crm.text}>
                    {hasReference ? "Outros atributos (manual)" : "Atributos (manual)"}
                  </Text>
                  <Button
                    size="sm"
                    variant="outline"
                    leftIcon={<FaPlus />}
                    color={crm.brand}
                    borderColor={crm.border}
                    _hover={{ bg: crm.brandSoft }}
                    onClick={() => setManual((m) => [...m, { attr: "", spec: "", description: "" }])}
                  >
                    Adicionar
                  </Button>
                </Flex>
                {manual.length === 0 && (
                  <Text fontSize="13px" color={crm.textFaint}>
                    Nenhum atributo manual.
                  </Text>
                )}
                {manual.map((m, index) => (
                  <Flex key={index} gap="8px" mb="8px" align="flex-end" wrap="wrap">
                    <Box w="90px">
                      <Text {...labelProps}>Atributo</Text>
                      <Input
                        {...inputProps}
                        value={m.attr}
                        maxLength={2}
                        placeholder="AB"
                        onChange={(e) => updateManual(index, { attr: e.target.value.toUpperCase() })}
                      />
                    </Box>
                    <Box w="110px">
                      <Text {...labelProps}>Especificação</Text>
                      <Input
                        {...inputProps}
                        value={m.spec}
                        maxLength={4}
                        placeholder="0001"
                        onChange={(e) => updateManual(index, { spec: e.target.value.replace(/\D/g, "") })}
                      />
                    </Box>
                    <Box flex="1" minW="160px">
                      <Text {...labelProps}>Descrição</Text>
                      <Input
                        {...inputProps}
                        value={m.description}
                        maxLength={500}
                        onChange={(e) => updateManual(index, { description: e.target.value })}
                      />
                    </Box>
                    <IconButton
                      aria-label="remover"
                      icon={<FaTrash />}
                      variant="ghost"
                      color={crm.textMuted}
                      _hover={{ color: crm.danger, bg: crm.subtle }}
                      onClick={() => setManual((rows) => rows.filter((_, i) => i !== index))}
                    />
                  </Flex>
                ))}
              </Box>
            </>
          )}
        </ModalBody>
        <ModalFooter borderTop={`1px solid ${crm.border}`} gap="8px">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            bg={crm.brand}
            color="white"
            _hover={{ bg: crm.brandDark }}
            isLoading={saving}
            isDisabled={loading}
            onClick={save}
          >
            Salvar NVE
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default NveModal;
